import React, { useEffect } from 'react'
import { Checkbox, Col, Radio, Row, Table, Typography } from 'antd'
import { FormVoucherInfoProps } from './InterfaceForm'
import {
  AdminsEcommerceGetProductCategoryItem,
  AdminsEcommerceInventoryStockLocationListResponseItem,
  AppTypeEnum,
  PaymentMethodEnum,
  StockLocationStatusEnum,
  UserRoleEnum,
} from 'generated/api'
import { ColumnsType } from 'antd/es/table'
import Card from 'components/card/Card'
import BRIVA from 'assets/images/icons/BRIVA'
import MandiriVA from 'assets/images/icons/MandiriVA'
import { classNameRadioGroup } from 'helpers/formik/classNameForm'

interface FormSection3Props extends FormVoucherInfoProps {
  statusFetchStockLocations: 'loading' | 'failed' | 'idle'
  statusFetchCategories: 'loading' | 'failed' | 'idle'
  stockLocations: AdminsEcommerceInventoryStockLocationListResponseItem[]
  categories: AdminsEcommerceGetProductCategoryItem[]
  fetchStockLocations: () => void
  fetchCategories: () => void
  resetFetchStockLocation: () => void
  tableContainerRef: React.MutableRefObject<HTMLDivElement | null>
  tableHeight: number
}

export default function FormSection3(props: FormSection3Props) {
  useEffect(() => {
    if (!props.formikProps.values.isAllStore) {
      props.fetchStockLocations()
    } else {
      props.resetFetchStockLocation()
    }
  }, [props.formikProps.values.isAllStore])

  useEffect(() => {
    if (!props.formikProps.values.isAllCategory) {
      props.fetchCategories()
    }
  }, [props.formikProps.values.isAllCategory])

  const loadingStockLocations = props.statusFetchStockLocations === 'loading'
  const loadingCategories = props.statusFetchCategories === 'loading'

  const appTypes = [
    {
      label: 'Agen',
      value: AppTypeEnum.Member,
    },
    {
      label: 'Pekerja',
      value: AppTypeEnum.Worker,
    },
    {
      label: 'Petani',
      value: AppTypeEnum.Petani,
    },
    {
      label: 'Duta',
      value: AppTypeEnum.Duta,
    },
  ]

  const userRoles = [
    {
      label: 'Agen',
      value: UserRoleEnum.Agent,
    },
    {
      label: 'Petani',
      value: UserRoleEnum.Farmer,
    },
    {
      label: 'Pemanen',
      value: UserRoleEnum.Harvester,
    },
    {
      label: 'Koordinator Lapangan',
      value: UserRoleEnum.FieldCoordinator,
    },
    {
      label: 'Pengemudi',
      value: UserRoleEnum.Driver,
    },
  ]

  const paymentMethods = [
    {
      icon: <BRIVA />,
      label: 'BRIVA',
      value: PaymentMethodEnum.Briva,
    },
    {
      icon: <MandiriVA />,
      label: 'MANDIRI VA',
      value: PaymentMethodEnum.Mandiriva,
    },
  ]

  const radioAllStores = [
    {
      value: true,
      additionalClassName: classNameRadioGroup(props.formikProps.values.isAllStore, true),
      title: 'Semua Lokasi',
      subtitle: 'Voucher akan berlaku untuk semua lokasi pengambilan.',
    },
    {
      value: false,
      additionalClassName: classNameRadioGroup(props.formikProps.values.isAllStore, false),
      title: 'Pilihan Lokasi',
      subtitle: 'Voucher hanya akan berlaku untuk lokasi-lokasi yang kamu pilih.',
    },
  ]

  const radioAllCategories = [
    {
      value: true,
      additionalClassName: classNameRadioGroup(props.formikProps.values.isAllCategory, true),
      title: 'Semua Kategori Voucher',
      subtitle: 'Semua kategori voucher akan berlaku untuk semua produk yang ada di tokomu.',
    },
    {
      value: false,
      additionalClassName: classNameRadioGroup(props.formikProps.values.isAllCategory, false),
      title: 'Pilih Kategori Voucher',
      subtitle: 'Kategori voucher hanya akan berlaku untuk Kategori yang kamu pilih.',
    },
  ]

  const clickCheckBoxAllAppTypes = () => {
    if (appTypes.every(appType => props.formikProps.values.appTypes.includes(appType.value))) {
      props.formikProps.setFieldValue('appTypes', [])
    } else {
      props.formikProps.setFieldValue(
        'appTypes',
        appTypes.map(appType => appType.value),
      )
    }
  }

  const clickCheckBoxAppType = (value: AppTypeEnum) => {
    if (props.formikProps.values.appTypes.includes(value)) {
      props.formikProps.setFieldValue(
        'appTypes',
        props.formikProps.values.appTypes.filter(appType => appType !== value),
      )
    } else {
      props.formikProps.setFieldValue('appTypes', [...props.formikProps.values.appTypes, value])
    }
  }

  const clickCheckBoxAllUserRoles = () => {
    if (userRoles.every(userRole => props.formikProps.values.userRoles.includes(userRole.value))) {
      props.formikProps.setFieldValue('userRoles', [])
    } else {
      props.formikProps.setFieldValue(
        'userRoles',
        userRoles.map(userRole => userRole.value),
      )
    }
  }

  const clickCheckBoxUserRole = (value: UserRoleEnum) => {
    if (props.formikProps.values.userRoles.includes(value)) {
      props.formikProps.setFieldValue(
        'userRoles',
        props.formikProps.values.userRoles.filter(userRole => userRole !== value),
      )
    } else {
      props.formikProps.setFieldValue('userRoles', [...props.formikProps.values.userRoles, value])
    }
  }

  const clickCheckBoxAllPaymentMethods = () => {
    if (
      paymentMethods.every(paymentMethod =>
        props.formikProps.values.paymentMethods.includes(paymentMethod.value),
      )
    ) {
      props.formikProps.setFieldValue('paymentMethods', [])
    } else {
      props.formikProps.setFieldValue(
        'paymentMethods',
        paymentMethods.map(paymentMethod => paymentMethod.value),
      )
    }
  }

  const clickCheckBoxPaymentMethod = (value: PaymentMethodEnum) => {
    if (props.formikProps.values.paymentMethods.includes(value)) {
      props.formikProps.setFieldValue(
        'paymentMethods',
        props.formikProps.values.paymentMethods.filter(paymentMethod => paymentMethod !== value),
      )
    } else {
      props.formikProps.setFieldValue('paymentMethods', [
        ...props.formikProps.values.paymentMethods,
        value,
      ])
    }
  }

  const rowSelection = {
    onChange: (
      _selectedRowKeys: React.Key[],
      selectedRows: AdminsEcommerceInventoryStockLocationListResponseItem[],
    ) => {
      const selected = selectedRows.map(item => ({
        id: item.id,
        name: item.name,
      }))

      props.formikProps.setFieldValue('stores', selected)
    },
  }

  const columnsOrder: ColumnsType<AdminsEcommerceInventoryStockLocationListResponseItem> = [
    {
      title: (
        <Typography.Text className="font-semibold text-sm text-[#333539]">
          Nama Lokasi
        </Typography.Text>
      ),
      dataIndex: 'name',
      key: 'name',
      render: stockLocationName => (
        <Typography.Text className="font-normal text-sm text-[#333539]">
          {stockLocationName}
        </Typography.Text>
      ),
    },
    {
      title: (
        <Typography.Text className="font-semibold text-sm text-[#333539]">Kota</Typography.Text>
      ),
      dataIndex: 'city',
      key: 'city',
      render: stockLocationCity => (
        <Typography.Text className="font-normal text-sm text-[#333539]">
          {stockLocationCity}
        </Typography.Text>
      ),
    },
    {
      title: (
        <Typography.Text className="font-semibold text-sm text-[#333539]">Status</Typography.Text>
      ),
      dataIndex: 'status',
      key: 'status',
      render: (status: StockLocationStatusEnum) => (
        <Typography.Text className="font-normal text-sm text-[#333539]">
          {status === StockLocationStatusEnum.Active ? 'Aktif' : 'Tidak Aktif'}
        </Typography.Text>
      ),
    },
  ]

  return (
    <Row className="flex flex-col gap-6">
      <Row gutter={[24, 24]}>
        <Col span={24} lg={6} className="self-center">
          <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
            Voucher untuk Platform
          </Typography.Paragraph>
          <Typography.Paragraph className="font-normal text-xs my-0">
            Pilih di Aplikasi apa saja Voucher dapat digunakan
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18} className="self-center">
          <Row gutter={8}>
            <Col span={24} lg={{ flex: '20%' }}>
              <Checkbox
                disabled={props.disabledForm}
                data-testid="checkbox_appTypes_all"
                className="px-2 py-4 shadow rounded-lg w-full"
                checked={appTypes.every(appType =>
                  props.formikProps.values.appTypes.includes(appType.value),
                )}
                onClick={clickCheckBoxAllAppTypes}
              >
                Semua
              </Checkbox>
            </Col>
            {appTypes.map(appType => (
              <Col span={24} lg={{ flex: '20%' }} key={appType.value}>
                <Checkbox
                  disabled={props.disabledForm}
                  data-testid={`checkbox_appTypes_${appType.value}`}
                  className="px-2 py-4 shadow rounded-lg w-full"
                  checked={props.formikProps.values.appTypes.includes(appType.value)}
                  onClick={() => clickCheckBoxAppType(appType.value)}
                >
                  {appType.label}
                </Checkbox>
              </Col>
            ))}
          </Row>
          {props.formikProps.errors.appTypes && props.formikProps.touched.appTypes ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_appTypes"
            >
              {props.formikProps.errors.appTypes}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <div className="border border-solid border-[#E5E5E7]" />
      <Row gutter={[24, 24]}>
        <Col span={24} lg={6} className="self-center">
          <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
            Voucher untuk Pengguna
          </Typography.Paragraph>
          <Typography.Paragraph className="font-normal text-xs my-0">
            Pilih jenis Pengguna yang dapat menggunakan Voucher
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18} className="self-center">
          <Row gutter={[8, 8]}>
            <Col span={24} lg={8}>
              <Checkbox
                disabled={props.disabledForm}
                data-testid="checkbox_userRoles_all"
                className="px-2 py-4 shadow rounded-lg w-full"
                checked={userRoles.every(userRole =>
                  props.formikProps.values.userRoles.includes(userRole.value),
                )}
                onClick={clickCheckBoxAllUserRoles}
              >
                Semua Pengguna
              </Checkbox>
            </Col>
            {userRoles.map(userRole => (
              <Col span={24} lg={8} key={userRole.value}>
                <Checkbox
                  disabled={props.disabledForm}
                  data-testid={`checkbox_userRoles_${userRole.value}`}
                  className="px-2 py-4 shadow rounded-lg w-full"
                  checked={props.formikProps.values.userRoles.includes(userRole.value)}
                  onClick={() => clickCheckBoxUserRole(userRole.value)}
                >
                  {userRole.label}
                </Checkbox>
              </Col>
            ))}
          </Row>
          {props.formikProps.errors.userRoles && props.formikProps.touched.userRoles ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_userRoles"
            >
              {props.formikProps.errors.userRoles}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <div className="border border-solid border-[#E5E5E7]" />
      <Row gutter={[24, 24]}>
        <Col span={24} lg={6} className="self-center">
          <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
            Pilih Metode Pembayaran
          </Typography.Paragraph>
          <Typography.Paragraph className="font-normal text-xs my-0">
            Pilih Metode Pembayaran yang boleh digunakan untuk Voucher
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18} className="self-center">
          <Row gutter={[8, 8]}>
            <Col span={24} lg={8}>
              <Checkbox
                disabled={props.disabledForm}
                data-testid="checkbox_paymentMethods_all"
                className="px-2 py-4 shadow rounded-lg w-full  h-full flex items-center"
                checked={paymentMethods.every(paymentMethod =>
                  props.formikProps.values.paymentMethods.includes(paymentMethod.value),
                )}
                onClick={clickCheckBoxAllPaymentMethods}
              >
                <Typography.Text className="text-inherit text-base font-medium">
                  Semua Pembayaran
                </Typography.Text>
              </Checkbox>
            </Col>
            {paymentMethods.map(paymentMethod => (
              <Col span={24} lg={8} key={paymentMethod.value}>
                <Checkbox
                  disabled={props.disabledForm}
                  data-testid={`checkbox_paymentMethods_${paymentMethod.value}`}
                  className="px-2 py-4 shadow rounded-lg w-full  h-full flex items-center"
                  checked={props.formikProps.values.paymentMethods.includes(paymentMethod.value)}
                  onClick={() => clickCheckBoxPaymentMethod(paymentMethod.value)}
                >
                  <div className="flex gap-4">
                    <div>{paymentMethod.icon}</div>
                    <Typography.Text className="text-base font-medium text-inherit self-center">
                      {paymentMethod.label}
                    </Typography.Text>
                  </div>
                </Checkbox>
              </Col>
            ))}
          </Row>
          {props.formikProps.errors.paymentMethods && props.formikProps.touched.paymentMethods ? (
            <Typography.Text
              className="text-[#FF4D4D] text-xs font-normal"
              data-testid="error_paymentMethods"
            >
              {props.formikProps.errors.paymentMethods}
            </Typography.Text>
          ) : null}
        </Col>
      </Row>
      <div className="border border-solid border-[#E5E5E7]" />
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6} className="lg:flex lg:self-center">
          <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
            Pilih Lokasi
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18}>
          <Radio.Group
            value={props.formikProps.values.isAllStore}
            onChange={e => {
              const value = e.target.value
              props.formikProps.setFieldValue('isAllStore', value)
              props.formikProps.setFieldValue('stores', [])
            }}
            disabled={props.disabledForm}
          >
            <Row gutter={24}>
              {radioAllStores.map(item => (
                <Col span={12} key={item.title}>
                  <Radio key={item.title} value={item.value} className={item.additionalClassName}>
                    <Typography.Paragraph className="font-semibold text-base my-0 text-[#3C5337]">
                      {item.title}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="font-normal text-xs my-0">
                      {item.subtitle}
                    </Typography.Paragraph>
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Col>
      </Row>
      {props.formikProps.values.isAllStore ? null : (
        <div className="border border-solid border-[#E5E5E7]" />
      )}
      {props.formikProps.values.isAllStore ? null : (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Typography.Paragraph className="mb-0 font-bold text-base text-[#333539]">
              Pilih Lokasi Toko untuk Voucher Rules
            </Typography.Paragraph>
            <Typography.Paragraph className="mb-0 font-medium text-xs text-[#5B6573]">
              Personalisasi Penggunaan Voucher dengan Menentukan Lokasi Preferensi Anda
            </Typography.Paragraph>
          </Col>
          <Col span={24}>
            <div className="border border-solid border-[#E5E5E7]" />
          </Col>
          <Col span={24}>
            <Typography.Paragraph className="mb-0 font-bold text-base text-[#333539]">
              Tabel Lokasi Toko
            </Typography.Paragraph>
          </Col>
          <Col
            span={24}
            ref={props.tableContainerRef}
            style={{ overflowY: 'auto', maxHeight: `${props.tableHeight}px` }}
          >
            <Table
              rowKey="id"
              data-testid="table_stockLocation"
              className="table-primary-admin checkbox-admin"
              pagination={false}
              rowSelection={{
                type: 'checkbox',
                getCheckboxProps: record => ({
                  disabled: !!props.disabledForm,
                  name: record.name,
                }),
                ...rowSelection,
                hideSelectAll: true,
                selectedRowKeys: props.formikProps.values.stores.map(item => item.id),
              }}
              loading={loadingStockLocations}
              columns={columnsOrder}
              dataSource={props.stockLocations}
            />
          </Col>
        </Row>
      )}
      <div className="border border-solid border-[#E5E5E7]" />
      <Row gutter={[8, 8]}>
        <Col span={24} lg={6} className="lg:flex lg:self-center">
          <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
            Atur Kategori
          </Typography.Paragraph>
        </Col>
        <Col span={24} lg={18}>
          <Radio.Group
            value={props.formikProps.values.isAllCategory}
            onChange={e => {
              const value = e.target.value
              props.formikProps.setFieldValue('isAllCategory', value)
              props.formikProps.setFieldValue('categories', [])
            }}
            disabled={props.disabledForm}
          >
            <Row gutter={24}>
              {radioAllCategories.map(item => (
                <Col span={12} key={item.title}>
                  <Radio value={item.value} className={item.additionalClassName}>
                    <Typography.Paragraph className="font-semibold text-base my-0 text-[#3C5337]">
                      {item.title}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="font-normal text-xs my-0">
                      {item.subtitle}
                    </Typography.Paragraph>
                  </Radio>
                </Col>
              ))}
            </Row>
          </Radio.Group>
        </Col>
      </Row>
      {props.formikProps.values.isAllCategory ? null : (
        <div className="border border-solid border-[#E5E5E7]" />
      )}
      {props.formikProps.values.isAllCategory ? null : (
        <Row gutter={24}>
          <Col span={24} lg={6} className="self-center">
            <Typography.Paragraph className="font-bold text-[#333539] text-base mb-0">
              Pilih Kategori
            </Typography.Paragraph>
            <Typography.Paragraph className="font-normal text-xs my-0">
              Pilih jenis kategori yang dapat menggunakan Voucher
            </Typography.Paragraph>
          </Col>
          <Col span={24} lg={18}>
            <Card
              loading={loadingCategories}
              spinnerTestId="loader_categories"
              dataTestId="card_categories"
            >
              {props.categories.length ? (
                <Row gutter={[12, 12]}>
                  {props.categories.map(category => (
                    <Col key={category.id} span={12}>
                      <Checkbox
                        checked={
                          !!props.formikProps.values.categories.find(c => c.id === category.id)
                        }
                        className="px-2 py-4 shadow rounded-lg w-full"
                        disabled={props.disabledForm}
                        onClick={() => {
                          const checkedCategories = props.formikProps.values.categories
                          if (checkedCategories.includes(category)) {
                            props.formikProps.setFieldValue(
                              'categories',
                              checkedCategories.filter(item => item !== category),
                            )
                          } else {
                            props.formikProps.setFieldValue('categories', [
                              ...checkedCategories,
                              category,
                            ])
                          }
                        }}
                      >
                        {category.name}
                      </Checkbox>
                    </Col>
                  ))}
                </Row>
              ) : null}
            </Card>
          </Col>
        </Row>
      )}
    </Row>
  )
}

FormSection3.defaultProps = {
  disabledForm: false,
}
